import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { averageMetric, trainingMinutes } from '../utils/resultMetrics';
import {
  ArrowLeft,
  TrendingUp,
  Clock,
  Target,
  Brain,
  Hand,
  Calendar,
  Award,
  FileText,
  Sparkles,
  Trophy,
} from 'lucide-react';

const isImageAvatar = (avatar?: string) =>
  Boolean(avatar) && (avatar!.startsWith('data:image') || avatar!.startsWith('http') || avatar!.startsWith('/avatars/'));

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { levelResults, consecutiveDays, badges, playerName, playerAvatar } = useGameStore();

  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const getCompletedAt = (value: unknown) => (typeof value === 'string' ? value : '');

  const weekResults = levelResults.filter((result) => {
    const completedAt = getCompletedAt(result?.completedAt);
    return completedAt ? new Date(completedAt) > weekAgo : false;
  });
  const totalSessions = weekResults.length;
  const estimatedTime = trainingMinutes(weekResults);

  const avgAccuracy = averageMetric(weekResults, 'accuracy');
  const avgReaction = averageMetric(weekResults, 'reactionTime');

  const avgScore =
    weekResults.length > 0
      ? weekResults.reduce((sum, item) => sum + item.score, 0) / weekResults.length
      : 0;

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(now.getTime() - (6 - i) * 24 * 60 * 60 * 1000);
    const dayStr = date.toISOString().split('T')[0];
    const dayResults = levelResults.filter((result) => {
      const completedAt = getCompletedAt(result?.completedAt);
      return completedAt ? completedAt.startsWith(dayStr) : false;
    });

    return {
      day: date.toLocaleDateString('zh-CN', { weekday: 'short' }),
      sessions: dayResults.length,
      accuracy:
        dayResults.length > 0
          ? averageMetric(dayResults, 'accuracy')
          : 0,
    };
  });

  const maxSessions = Math.max(...chartData.map((item) => item.sessions), 1);

  const containerAnim = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const cardAnim = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { duration: 0.4 } },
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)]">
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">家长游戏面板</h1>
          <div className="w-9" />
        </div>
      </header>

      <motion.div
        variants={containerAnim}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-4"
      >
        <motion.div
          variants={cardAnim}
          className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-850 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          {/* Decorative background accent */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-emerald-100/40 to-transparent dark:from-emerald-900/20 rounded-bl-full pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 flex-shrink-0 overflow-hidden rounded-[14px] shadow-md">
              {isImageAvatar(playerAvatar) ? (
                <img src={playerAvatar} alt="头像" className="h-full w-full object-cover object-center scale-[1.2]" />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/40 dark:to-teal-900/40 text-3xl">
                  <span>{playerAvatar || '🧒'}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-800 dark:text-gray-100 font-bold text-xl truncate">
                {playerName || '小探险者'}
              </h3>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 px-2.5 py-1 rounded-full">
                  <Calendar className="w-3.5 h-3.5" /> 连续 {consecutiveDays} 天
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/40 px-2.5 py-1 rounded-full">
                  <Award className="w-3.5 h-3.5" /> {badges.length} 枚成就
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardAnim} className="grid grid-cols-2 gap-3">
          {[
            {
              icon: FileText,
              title: '游戏训练报告',
              desc: '查看周度训练表现与完成情况',
              path: '/parent/eye-report',
              color: 'from-emerald-500 to-teal-500',
            },
            {
              icon: Sparkles,
              title: '训练计划推荐',
              desc: '按最近训练数据智能推荐玩法',
              path: '/parent/training-plan',
              color: 'from-amber-500 to-orange-500',
            },
            {
              icon: Award,
              title: '训练成就中心',
              desc: '查看孩子已解锁的奖励进度',
              path: '/achievements',
              color: 'from-rose-500 to-pink-500',
            },
            {
              icon: Trophy,
              title: '排行榜表现',
              desc: '查看不同维度的训练排行',
              path: '/leaderboard',
              color: 'from-violet-500 to-purple-500',
            },
          ].map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={`bg-gradient-to-r ${item.color} rounded-2xl p-4 shadow-md text-left hover:shadow-lg transition-all active:scale-[0.98]`}
            >
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-2.5">
                <item.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white font-semibold text-sm">{item.title}</h3>
              <p className="text-white/75 text-[11px] mt-1 leading-relaxed">{item.desc}</p>
            </button>
          ))}
        </motion.div>

        <motion.div variants={cardAnim} className="grid grid-cols-4 gap-3">
          {[
            {
              icon: Target,
              label: '平均准确率',
              value: `${Math.round(avgAccuracy)}%`,
              color: 'text-emerald-600',
              bg: 'bg-emerald-50',
            },
            {
              icon: TrendingUp,
              label: '平均得分',
              value: `${Math.round(avgScore)}`,
              color: 'text-blue-600',
              bg: 'bg-blue-50',
            },
            {
              icon: Clock,
              label: '训练次数',
              value: `${totalSessions}次`,
              color: 'text-orange-600',
              bg: 'bg-orange-50',
            },
            {
              icon: Hand,
              label: '训练时长',
              value: `${estimatedTime}分`,
              color: 'text-violet-600',
              bg: 'bg-violet-50',
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-sm border border-gray-100 dark:border-gray-700 text-center"
            >
              <div
                className={`w-8 h-8 ${stat.bg} dark:bg-opacity-20 rounded-xl flex items-center justify-center mx-auto mb-2`}
              >
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <p className="text-base font-bold text-gray-800 dark:text-gray-100">{stat.value}</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">周度训练完成曲线</h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">最近7天</span>
          </div>
          <div className="flex items-end justify-between h-28 gap-1.5">
            {chartData.map((item, i) => (
              <div key={i} className="flex flex-col items-center flex-1 gap-1.5">
                <motion.div
                  className="w-full rounded-lg bg-gradient-to-t from-emerald-400 to-teal-300"
                  initial={{ height: 0 }}
                  animate={{ height: `${(item.sessions / maxSessions) * 100}%` }}
                  transition={{ delay: 0.3 + i * 0.06, duration: 0.5, ease: 'easeOut' }}
                  style={{ minHeight: item.sessions > 0 ? '10px' : '3px' }}
                />
                <span className="text-[10px] text-gray-400">{item.day}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">核心训练能力</h3>
          <div className="space-y-4">
            {[
              {
                icon: Target,
                label: '专注稳定度',
                value: Math.round(Math.min(100, avgAccuracy * 0.9 + 10)),
                color: 'from-emerald-400 to-teal-400',
              },
              {
                icon: Brain,
                label: '记忆与判断',
                value: Math.round(Math.min(100, avgAccuracy * 0.75 + 25)),
                color: 'from-blue-400 to-indigo-400',
              },
              {
                icon: Hand,
                label: '反应与操作',
                value: Math.round(Math.max(20, Math.min(100, 100 - avgReaction / 20))),
                color: 'from-amber-400 to-orange-400',
              },
            ].map((item, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <item.icon className="w-3.5 h-3.5" /> {item.label}
                  </span>
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{item.value}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full bg-gradient-to-r ${item.color} rounded-full`}
                    initial={{ width: 0 }}
                    animate={{ width: `${item.value}%` }}
                    transition={{ delay: 0.5 + idx * 0.15, duration: 0.8, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={cardAnim}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">本周游戏建议</h3>
          <div className="space-y-3">
            {[
              {
                emoji: '🎯',
                title: '训练频率建议',
                desc:
                  totalSessions < 7
                    ? '本周训练次数偏少，建议每天安排1-2次短时训练。'
                    : '训练频率很稳定，保持现在的节奏即可。',
              },
              {
                emoji: '⚡',
                title: '反应速度建议',
                desc:
                  avgReaction > 850
                    ? '可增加追踪和快速点击类游戏，帮助提升即时反应。'
                    : '反应速度表现不错，可以加入更高难度挑战。',
              },
              {
                emoji: '🏅',
                title: '成长激励建议',
                desc:
                  badges.length < 3
                    ? '可以优先完成简单目标，帮助孩子更快获得首批成就。'
                    : '已积累不少成就，建议冲刺连续完成类奖励。',
              },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-700 rounded-xl p-3.5">
                <span className="text-lg flex-shrink-0">{item.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{item.title}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-2xl mx-auto flex items-center justify-center py-3 px-4">
          <button
            onClick={() => navigate('/')}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-200 hover:shadow-lg hover:shadow-emerald-300 transition-all active:scale-95"
          >
            返回训练大厅
          </button>
        </div>
      </nav>
    </div>
  );
}
